import {
  Ftni_UpdateFtniPaymentMutation,
  Ftni_UpdateFtniPaymentMutationVariables,
} from '@/types';
import { gql, useMutation } from '@apollo/client';
import { openFTNIDirectFormInBrowser } from '@/services/ftni';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useCallback } from 'react';
import { usePaymentsContext } from '@/contexts/payments-context';

export const FTNI_UPDATE_FTNI_PAYMENT_MUTATION = gql`
  mutation FTNI_UpdateFTNIPayment(
    $orderInvoiceUUIDs: [uuid!]!
    $userUUID: uuid!
  ) {
    updateFtniPayment(
      order_invoice_ids: $orderInvoiceUUIDs
      user_id: $userUUID
    ) {
      success
    }
  }
`;

/**
 * Hook for initiating FTNI Payment Session.
 *
 * Returns an async function that will begin the FTNI DirectForm
 * process in a new browser window/tab. The function raises
 * Errors on unexpected GraphQL response, and resolves when
 * the browser has been closed. The Order Invoice will always be unlocked
 * after the browser is closed.
 */
export const useInitiateFTNI = () => {
  const {
    user: { uuid: userUUID },
    selectedRestaurantUUID,
    selectedRestaurantRelationship: { supplierUUID },
  } = useAuthenticatedUserContext();
  const { refreshInvoices } = usePaymentsContext();

  const [updateFTNIPayment] = useMutation<
    Ftni_UpdateFtniPaymentMutation,
    Ftni_UpdateFtniPaymentMutationVariables
  >(FTNI_UPDATE_FTNI_PAYMENT_MUTATION);

  return useCallback(
    async (orderInvoiceNumbers: string[], orderInvoiceUUIDs: string[]) => {
      await openFTNIDirectFormInBrowser({
        orderInvoiceNumbers: orderInvoiceNumbers,
        orderInvoiceUUIDs: orderInvoiceUUIDs,
        restaurantUUID: selectedRestaurantUUID,
        supplierUUID: supplierUUID,
        userUUID: userUUID,
      });

      // Always unlock the invoices after browser is closed
      await updateFTNIPayment({
        variables: {
          orderInvoiceUUIDs,
          userUUID,
        },
      });

      await refreshInvoices();
    },
    [
      refreshInvoices,
      selectedRestaurantUUID,
      supplierUUID,
      updateFTNIPayment,
      userUUID,
    ],
  );
};
